import cx from 'classnames'
import { Formik } from 'formik'
import jwtDecode from 'jwt-decode'
import { useState } from 'react'
import Container from 'react-bootstrap/Container'
import { useIntl } from 'react-intl'

import AppleLoginBtn from 'components/Buttons/ButtonSocialLogin/AppleLoginBtn'
import FacebookLoginBtn from 'components/Buttons/ButtonSocialLogin/FacebookLoginBtn'
import GoogleLoginBtn from 'components/Buttons/ButtonSocialLogin/GoogleLoginBtn'
import ButtonCinestar from '../../Buttons/ButtonCinestar/ButtonCinestar'
import Checkbox from '../../Inputs/Checkbox/Checkbox'
import InputEmail from '../../Inputs/InputEmail/InputEmail'
import InputPassword from '../../Inputs/InputPassword/InputPassword'
import InputPhone from '../../Inputs/InputPhone/InputPhone'
import InputText from '../../Inputs/InputText/InputText'
import FormInputsContainer from '../FormInputsContainer/FormInputsContainer'
import FormLayout from '../FormLayout/FormLayout'
import FormLine from '../FormLine/FormLine'
import FormPasswordValidation from '../FormPasswordValidation/FormPasswordValidation'
import MemberBenefits from './components/MemberBenefits/MemberBenefits'
import MemberInputs from './components/MemberInputs/MemberInputs'

import { useValidationSchema } from './hooks/useValidationSchema'

import { INITIAL_VALUES, MEMBER_INITIAL_VALUES } from './constants'

import { documentSlugs } from 'components/Modal/DocumentPopupWindow/slugs'
import gtmSubmitRegistration from 'services/gtm/gtmSubmitRegistration'
import useGtmRegistrationViewed from 'services/gtm/useGtmRegistrationViewed'
import './FormRegister.scss'
import hocFormRegister from './hocFormRegister'

const FormRegister = ({ handleSubmit, handleSocialsError, className, loading, setSlug }) => {
    const [withMemberAccount, setWithMemberAccount] = useState(false)
    const [showMemberForm, setShowMemberForm] = useState(false)
    const intl = useIntl()

    const initialValues = {
        ...INITIAL_VALUES,
        ...MEMBER_INITIAL_VALUES,
    }

    const validationSchema = useValidationSchema(showMemberForm)
    useGtmRegistrationViewed()

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values) => {
                gtmSubmitRegistration(withMemberAccount ? 'ano' : 'ne')
                handleSubmit(values, withMemberAccount)
            }}
        >
            {(formik) => (
                <FormLayout
                    onSubmit={formik.handleSubmit}
                    className={cx('form-register d-flex flex-column', className)}
                    size="lg"
                >
                    <FormInputsContainer type={'grid'}>
                        <FacebookLoginBtn
                            onSuccess={(response) => {
                                const data = response

                                formik?.setFieldValue('firstName', data?.name?.split(' ')[0])
                                formik?.setFieldValue('lastName', data?.name?.split(' ')[1])
                                formik?.setFieldValue('email', data?.email)
                            }}
                            onError={handleSocialsError}
                        />

                        <GoogleLoginBtn
                            onSuccess={(response) => {
                                const data = jwtDecode(response?.credential)

                                formik?.setFieldValue('firstName', data?.given_name)
                                formik?.setFieldValue('lastName', data?.family_name)
                                formik?.setFieldValue('email', data?.email)
                            }}
                            onError={handleSocialsError}
                        />

                        <AppleLoginBtn
                            onSuccess={(response) => {
                                const data = jwtDecode(response.detail.authorization.id_token)
                                formik?.setFieldValue('email', data?.email)
                            }}
                        />
                    </FormInputsContainer>
                    <FormLine text={intl.formatMessage({ id: 'forms.register.orFillText' })} />
                    <FormInputsContainer>
                        <InputText
                            name={'firstName'}
                            id={'firstName'}
                            error={formik?.errors?.firstName}
                            touched={formik?.touched?.firstName}
                            {...formik.getFieldProps('firstName')}
                            label={intl.formatMessage({ id: 'forms.register.nameLabel' })}
                            placeholder={intl.formatMessage({ id: 'forms.register.namePlaceholder' })}
                            onBlur={formik?.handleBlur}
                        />
                        <InputText
                            name={'lastName'}
                            id={'lastName'}
                            error={formik?.errors?.lastName}
                            touched={formik?.touched?.lastName}
                            {...formik.getFieldProps('lastName')}
                            label={intl.formatMessage({ id: 'forms.register.surnameLabel' })}
                            placeholder={intl.formatMessage({ id: 'forms.register.surnamePlaceholder' })}
                            onBlur={formik?.handleBlur}
                        />
                    </FormInputsContainer>
                    <FormInputsContainer>
                        <InputPhone
                            name={'phone'}
                            id={'phone'}
                            error={formik?.errors?.phone}
                            touched={formik?.touched?.phone}
                            {...formik.getFieldProps('phone')}
                            label={intl.formatMessage({ id: 'forms.register.phoneLabel' })}
                            placeholder={intl.formatMessage({ id: 'forms.register.phonePlaceholder' })}
                            onBlur={formik?.handleBlur}
                        />
                        <InputEmail
                            name={'email'}
                            id={'email'}
                            error={formik?.errors?.email}
                            touched={formik?.touched?.email}
                            {...formik.getFieldProps('email')}
                            label={intl.formatMessage({ id: 'forms.register.emailLabel' })}
                            placeholder={intl.formatMessage({ id: 'forms.register.emailPlaceholder' })}
                            onBlur={formik?.handleBlur}
                        />
                    </FormInputsContainer>
                    <FormInputsContainer>
                        <Container fluid className="d-flex flex-column">
                            <InputPassword
                                name={'password'}
                                id={'password'}
                                {...formik.getFieldProps('password')}
                                error={formik?.errors?.password}
                                touched={formik?.touched?.password}
                                label={intl.formatMessage({ id: 'forms.register.passwordLabel' })}
                                placeholder={intl.formatMessage({ id: 'forms.register.passwordPlaceholder' })}
                                onBlur={formik?.handleBlur}
                            />
                            <FormPasswordValidation
                                value={formik?.values?.password}
                                touched={formik?.touched?.password}
                                email={formik?.values.email}
                            />
                        </Container>
                        <InputPassword
                            name={'controlPassword'}
                            id={'controlPassword'}
                            error={formik?.errors?.controlPassword}
                            touched={formik?.touched?.controlPassword}
                            {...formik.getFieldProps('controlPassword')}
                            label={intl.formatMessage({ id: 'forms.register.passwordConfLabel' })}
                            placeholder={intl.formatMessage({ id: 'forms.register.passwordConfPlaceholder' })}
                            onBlur={formik?.handleBlur}
                        />
                    </FormInputsContainer>
                    <Checkbox
                        name={'checkBoxTermsAndConditions'}
                        id={'checkBoxTermsAndConditions'}
                        error={formik?.errors?.checkBoxTermsAndConditions}
                        touched={formik?.touched?.checkBoxTermsAndConditions}
                        {...formik.getFieldProps('checkBoxTermsAndConditions')}
                        label={
                            <a onClick={() => setSlug({ slug: documentSlugs.conditions })} rel="noreferrer">
                                {intl.formatMessage(
                                    { id: 'forms.register.checkboxLabel' },
                                    { span: (chunks) => <span className="text-decoration-underline">{chunks}</span> }
                                )}
                            </a>
                        }
                        onBlur={formik?.handleBlur}
                    />

                    <FormInputsContainer>
                        <ButtonCinestar
                            variant={'secondary'}
                            type={'submit'}
                            name="submit-form"
                            disabled={loading}
                            onClick={() => setWithMemberAccount(false)}
                        >
                            {intl.formatMessage({ id: 'forms.register.buttonCreateBasicAccount' })}
                        </ButtonCinestar>
                        <ButtonCinestar
                            variant={'primary-v1'}
                            disabled={loading}
                            onClick={() => {
                                setShowMemberForm(!showMemberForm)
                                formik?.resetForm(formik)
                            }}
                        >
                            {intl.formatMessage({ id: 'forms.register.buttonCreateMemberAccount' })}
                        </ButtonCinestar>
                    </FormInputsContainer>

                    <Container fluid className="d-flex flex-column align-items-center">
                        {!showMemberForm ? (
                            <MemberBenefits />
                        ) : (
                            <MemberInputs
                                loading={loading}
                                setWithMemberAccount={setWithMemberAccount}
                                formik={formik}
                                setSlug={setSlug}
                            />
                        )}
                    </Container>
                </FormLayout>
            )}
        </Formik>
    )
}

export default hocFormRegister(FormRegister)
